import React, { useState, useEffect } from 'react';

const AboutSectionAdmin = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [features, setFeatures] = useState([{ text: '', strong: '' }]);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/about-section');
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch data');
      }
      
      setAboutData(result.data);
      setFeatures(result.data.features || [{ text: '', strong: '' }]);
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error fetching about section data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAboutData({
      ...aboutData,
      [name]: value
    });
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...features];
    newFeatures[index][field] = value;
    setFeatures(newFeatures);
  };

  const addFeature = () => {
    setFeatures([...features, { text: '', strong: '' }]);
  };

  const removeFeature = (index) => {
    if (features.length > 1) {
      const newFeatures = [...features];
      newFeatures.splice(index, 1);
      setFeatures(newFeatures);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const updatedData = {
        ...aboutData,
        features: features.filter(feature => feature.text.trim() !== '')
      };

      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/about-section', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update data');
      }
      
      setAboutData(result.data);
      setMessage({ text: 'About section updated successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error updating about section:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/about-section/toggle', {
        method: 'PATCH',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to toggle status');
      }
      
      setAboutData(result.data);
      setMessage({ 
        text: `About section ${result.data.isActive ? 'activated' : 'deactivated'} successfully!`, 
        type: 'success' 
      });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error toggling about section status:', err);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default data? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/about-section/reset', {
        method: 'POST',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to reset data');
      }
      
      setAboutData(result.data);
      setFeatures(result.data.features || [{ text: '', strong: '' }]);
      setMessage({ text: 'About section reset to default successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error resetting about section:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Manage About Section</h2>
      
      {message.text && (
        <div className={`alert alert-${message.type}`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title">Section Status</h5>
            <button 
              className={`btn ${aboutData?.isActive ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleActive}
            >
              {aboutData?.isActive ? 'Deactivate Section' : 'Activate Section'}
            </button>
          </div>
          <p className="card-text">
            Current status: <span className={`badge bg-${aboutData?.isActive ? 'success' : 'danger'}`}>
              {aboutData?.isActive ? 'Active' : 'Inactive'}
            </span>
          </p>
          <button className="btn btn-outline-danger" onClick={handleReset}>
            Reset to Default
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Content Management</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="title" className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                id="title"
                name="title"
                value={aboutData?.title || ''}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="highlightedText" className="form-label">Highlighted Text</label>
              <input
                type="text"
                className="form-control"
                id="highlightedText"
                name="highlightedText"
                value={aboutData?.highlightedText || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                rows="3"
                value={aboutData?.description || ''}
                onChange={handleInputChange}
              ></textarea>
            </div>

            <div className="mb-3">
              <label htmlFor="imageUrl" className="form-label">Image URL</label>
              <input
                type="text"
                className="form-control"
                id="imageUrl"
                name="imageUrl"
                value={aboutData?.imageUrl || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="imageAlt" className="form-label">Image Alt Text</label>
              <input
                type="text"
                className="form-control"
                id="imageAlt"
                name="imageAlt"
                value={aboutData?.imageAlt || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Features</h5>
            <button type="button" className="btn btn-sm btn-primary" onClick={addFeature}>
              Add Feature
            </button>
          </div>
          <div className="card-body">
            {features.map((feature, index) => (
              <div key={index} className="row mb-3">
                <div className="col-md-5">
                  <label className="form-label">Feature Text</label>
                  <input
                    type="text"
                    className="form-control"
                    value={feature.text}
                    onChange={(e) => handleFeatureChange(index, 'text', e.target.value)}
                    placeholder="Feature text"
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label">Feature Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={feature.strong}
                    onChange={(e) => handleFeatureChange(index, 'strong', e.target.value)}
                    placeholder="Feature description"
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeFeature(index)}
                    disabled={features.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <button type="button" className="btn btn-secondary" onClick={fetchAboutData}>
            Reset Changes
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutSectionAdmin;